/* eslint-disable @typescript-eslint/no-explicit-any */
import type { IPlaceMinimal } from '@/hooks/place/usePlacesMinimal';
import type {
  IMotoboy,
  IMotoboyPlaceDedication,
  IMotoboyMinimalInfo,
  IPlaceMinimalInfo,
  IMember,
} from '@/Interfaces/Data';
import type { IResponseRequest, DtoRegistrationMember } from '@/Interfaces/Dto';
import {
  stringifyQueryString,
  stringifyQueryStringArray,
} from '@/Utils/FnUtils';
import { apiClientOld } from '@/_config/lib/axios/api-client-old';
import type { PlaceFormFields } from '@/_features/places/types';

const Requests = {
  manager: {
    login: (login: string, senha: string) => {
      return apiClientOld.post(`/managers/login`, { login, senha });
    },
  },
  motoboys: {
    getMotoboyId: (motoboyId: string) => {
      return apiClientOld.get(`/entregadores/${motoboyId}`);
    },
    createMotoboy: (payload: any) => {
      return apiClientOld.post(`/entregadores/by-manager`, {
        ...payload,
      }) as Promise<IResponseRequest<IMotoboy>>;
    },
    editMotoboy: (motoboyId: string, payload: any) => {
      return apiClientOld.put(`/entregadores/${motoboyId}/by-manager`, {
        ...payload,
      }) as Promise<IResponseRequest<boolean>>;
    },
    motoboyReset: (motoboyId: string) => {
      return apiClientOld.post(`/entregadores/${motoboyId}/panic`);
    },
    getDedications: (motoboyId: string) => {
      return apiClientOld.get(`/entregadores/${motoboyId}/dedications`);
    },
    updateDedicationByPlace: (
      motoboyId: string,
      placeId: string,
      dedication: IMotoboyPlaceDedication,
    ) => {
      return apiClientOld.put(
        `/entregadores/${motoboyId}/dedicate/${placeId}`,
        dedication,
      ) as Promise<IResponseRequest<any>>;
    },
    deleteDedicationByPlace: (motoboyId: string, placeId: string) => {
      return apiClientOld.delete(
        `/entregadores/${motoboyId}/remove-dedicate/${placeId}`,
      ) as Promise<IResponseRequest<any>>;
    },
    getMotoboysMinimalInfo: ({
      franchiseId,
      franchiseIds,
    }: {
      franchiseId?: string;
      franchiseIds?: string[];
    }) => {
      const request: Promise<IResponseRequest<IMotoboyMinimalInfo[]>> =
        apiClientOld.get(
          `/entregadores/name?${stringifyQueryString({
            municipioId: franchiseId,
            municipioIds: franchiseIds,
          })}`,
        );

      return request;
    },
  },
  deliveries: {
    reopenDelivery: (deliveryId: string) => {
      return apiClientOld.post(`/entregas/${deliveryId}/re-open`);
    },
    cancelDelivery: (
      deliveryId: string,
      collectTaxOfPlace: boolean,
      passOnTaxForMotoboy: boolean,
    ) => {
      return apiClientOld.post(
        `/entregas/${deliveryId}/cancel?cobrarTaxaEstabelecimento=${collectTaxOfPlace}&repassarParteParaEntregador=${passOnTaxForMotoboy}`,
      );
    },
    associateMotoboy: (managerId: string, motoboyId: string) => {
      return apiClientOld.post(
        `/entregas/${managerId}/associate-by-manager/${motoboyId}`,
      );
    },
    arriveAtPlace: (motoboyId: string) => {
      return apiClientOld.post(`/jackson/${motoboyId}/chegou-na-loja`);
    },
    acceptDelivery: (motoboyId: string, deliveryId: string) => {
      return apiClientOld.post(
        `/jackson/${motoboyId}/accept?${stringifyQueryString({
          deliveryId,
        })}`,
      );
    },
    rejectDelivery: (motoboyId: string, deliveryId: string) => {
      return apiClientOld.post(
        `/jackson/${motoboyId}/reject?${stringifyQueryString({
          deliveryId,
        })}`,
      );
    },
    getOutPlace: (motoboyId: string) => {
      return apiClientOld.put(
        `/jackson/${motoboyId}/left-the-store-by-manager`,
      );
    },
    deliverDelivery: (motoboyId: string, deliveryId: string) => {
      return apiClientOld.put(
        `/jackson/${motoboyId}/delivered-by-manager/?${stringifyQueryString({
          deliveryId,
        })}`,
      );
    },
  },
  bank: {
    launchCreditDebitPlace: (
      placeId: string,
      descricao: string,
      cents: number,
      manager: string,
    ) => {
      return apiClientOld.post(`/estabelecimentos/${placeId}/bank/transacao`, {
        descricao,
        cents,
        customFields: {
          manager,
        },
      });
    },
    launchCreditDebitMotoboy: (
      motoboyId: string,
      descricao: string,
      cents: number,
      manager: string,
    ) => {
      return apiClientOld.post(`/entregadores/${motoboyId}/bank/transacao`, {
        descricao,
        cents,
        customFields: {
          manager,
        },
      });
    },
  },
  places: {
    editPlace: (placeId: string, payload: PlaceFormFields) => {
      return apiClientOld.put(`/estabelecimentos/${placeId}`, payload);
    },
    getPlacesMinimalList: () => {
      return apiClientOld.get(`/estabelecimentos/to-dedicate`) as Promise<
        IResponseRequest<IPlaceMinimal[]>
      >;
    },
    updateIsOnlyDedicatedMotoboys: (
      placeId: string,
      isOnlyDedicateMotoboys: boolean,
    ) => {
      return apiClientOld.put(`/estabelecimentos/${placeId}/set-dedicate`, {
        hasOnlyDeliverymenDedicated: isOnlyDedicateMotoboys,
      }) as Promise<IResponseRequest<boolean>>;
    },
    getPlacesMinimalInfo: ({
      franchiseId,
      franchiseIds,
    }: {
      franchiseId?: string;
      franchiseIds?: string[];
    }) => {
      const request: Promise<IResponseRequest<IPlaceMinimalInfo[]>> =
        apiClientOld.get(
          `/estabelecimentos/name?${stringifyQueryString({
            municipioId: franchiseId,
            municipioIds: franchiseIds,
          })}`,
        );

      return request;
    },
    updateIsPlaceFareActive: (placeId: string, isPlaceFareActive: boolean) => {
      return apiClientOld.put(
        `/estabelecimentos/${placeId}/fare/set-is-place-fare-active`,
        {
          isPlaceFareActive,
        },
      ) as Promise<IResponseRequest<void>>;
    },
    deletePlaceFare: (placeId: string) => {
      return apiClientOld.delete(
        `/estabelecimentos/${placeId}/fare/delete-fare`,
      ) as Promise<IResponseRequest<void>>;
    },
  },
  members: {
    getMembers: (
      limit: number,
      offset?: number | string,
      nome?: string,
      municipioId?: string,
      isAtivo?: boolean,
    ) =>
      apiClientOld.get(
        `/managers?${stringifyQueryStringArray({
          limit,
          offset,
          nome,
          municipioId,
          isAtivo,
        })}`,
      ) as Promise<IResponseRequest<IMember[]>>,
    getMemberById: (memberId: string) =>
      apiClientOld.get(`/managers/${memberId}`) as Promise<
        IResponseRequest<IMember>
      >,
    setActive: (id: string, isAtivo: boolean) =>
      apiClientOld.put(`/managers/${id}/is-active`, {
        isAtivo,
      }) as Promise<void>,
    postRegistrationMember: (payload: DtoRegistrationMember) =>
      apiClientOld.post('/managers', payload) as Promise<
        IResponseRequest<IMember>
      >,
    putRegistrationMember: (id: string, payload: DtoRegistrationMember) =>
      apiClientOld.put(`/managers/${id}`, payload) as Promise<
        IResponseRequest<void>
      >,
  },
};

export default Requests;
